import React, { useState, useEffect } from 'react';
import { fsSet } from '../lib/firebase';
import {
  createAccessToken, revokeToken, listTokensForFamily, buildShareLink,
  defaultScopes, syncSignInEmail, SCOPE_META,
} from '../lib/tokens';

// ─── CAREGIVER ACCESS PANEL ────────────────────────────────────────────────────
// Lives inside a caregiver's own edit form (EditCaregivers.jsx). Access is a
// property of the person, not a separate "guest vs. sitter" link picker: pick
// what they can see, how long a link lasts, create/copy the link, and
// optionally let them sign in with Google using the same entitlements.

const EXPIRY_OPTIONS = [['30', '30 days'], ['90', '90 days'], ['365', '1 year'], ['', 'Never']];

export default function CaregiverAccessPanel({ familyData, setFamilyData, currentUser, sitter, onAccessChange }) {
  const access = sitter.access || { scopes: defaultScopes(), expiresDays: '90', googleSignInEnabled: false };
  const [tokens, setTokens]           = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [creating, setCreating]       = useState(false);
  const [copied, setCopied]           = useState(null);
  const [msg, setMsg]                 = useState(null);
  const [savingAccess, setSavingAccess] = useState(false);

  useEffect(() => {
    loadTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitter.id]);

  async function loadTokens() {
    setLoadingTokens(true);
    const all = await listTokensForFamily(familyData.id);
    setTokens(all.filter(t => t.caregiverId === sitter.id));
    setLoadingTokens(false);
  }

  async function saveAccess(next) {
    setSavingAccess(true);
    try {
      await fsSet(`families/${familyData.id}/sitters/${sitter.id}`, { access: next });
      setFamilyData(prev => ({
        ...prev,
        sitters: (prev.sitters || []).map(s => s.id === sitter.id ? { ...s, access: next } : s),
      }));
      await syncSignInEmail({
        familyId: familyData.id, email: sitter.email,
        enabled: next.googleSignInEnabled, familyData, setFamilyData,
      });
      onAccessChange?.({ ...sitter, access: next });
    } finally {
      setSavingAccess(false);
    }
  }

  function toggleScope(id) {
    saveAccess({ ...access, scopes: { ...access.scopes, [id]: !access.scopes[id] } });
  }

  function setExpiry(val) {
    saveAccess({ ...access, expiresDays: val });
  }

  function toggleGoogleSignIn() {
    saveAccess({ ...access, googleSignInEnabled: !access.googleSignInEnabled });
  }

  async function handleCreate() {
    setCreating(true);
    try {
      const doc = await createAccessToken({
        familyId:      familyData.id,
        caregiverId:   sitter.id,
        label:         `${sitter.firstName} ${sitter.lastName || ''}`.trim(),
        createdBy:     currentUser?.email || 'owner',
        expiresInDays: access.expiresDays ? parseInt(access.expiresDays) : null,
        scopes:        access.scopes,
      });
      setTokens(prev => [doc, ...prev]);
      await copyLink(doc.token);
      setMsg('Link created and copied to clipboard');
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setCreating(false);
    setTimeout(() => setMsg(null), 4000);
  }

  async function copyLink(token) {
    const link = buildShareLink(token);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(token);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      window.prompt('Copy this link:', link);
    }
  }

  async function handleRevoke(token) {
    if (!window.confirm('Revoke this link? Anyone using it will lose access immediately.')) return;
    await revokeToken(token);
    setTokens(prev => prev.filter(t => t.token !== token));
  }

  function fmtExpiry(doc) {
    if (!doc.expiresAt) return 'Never expires';
    const d = new Date(doc.expiresAt);
    const daysLeft = Math.ceil((d - Date.now()) / 86400000);
    if (daysLeft < 0) return 'Expired';
    if (daysLeft === 0) return 'Expires today';
    return `Expires in ${daysLeft}d`;
  }

  const enabledScopes = SCOPE_META.filter(m => access.scopes?.[m.id]);

  return (
    <div className="add-form" style={{ marginTop: 10 }}>
      <div className="child-form-section" style={{ paddingTop: 0, borderTop: 'none' }}>Access</div>
      {msg && <div className="settings-msg">{msg}</div>}

      <div>
        <label className="form-label">What {sitter.firstName || 'they'} can see</label>
        <div className="scope-toggles">
          {SCOPE_META.map(m => (
            <button key={m.id} type="button"
              className={`scope-toggle${access.scopes?.[m.id] ? ' scope-toggle--on' : ''}`}
              title={m.desc} disabled={savingAccess}
              onClick={() => toggleScope(m.id)}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="form-label">Link expires after</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {EXPIRY_OPTIONS.map(([val, lbl]) => (
            <button key={lbl} type="button"
              className={`range-btn${(access.expiresDays ?? '90') === val ? ' active' : ''}`}
              disabled={savingAccess}
              onClick={() => setExpiry(val)}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <p className="settings-note" style={{ margin: 0 }}>
        {enabledScopes.length
          ? `${sitter.firstName || 'They'} will see: ${enabledScopes.map(m => m.label).join(' · ')}`
          : 'Enable at least one tab above before creating a link'}
      </p>

      <button className="btn btn--primary btn--small"
        disabled={creating || enabledScopes.length === 0}
        onClick={handleCreate}>
        {creating ? 'Creating…' : '🌾 Create link & copy to clipboard'}
      </button>

      {!loadingTokens && tokens.length > 0 && (
        <div className="access-list">
          {tokens.map(t => (
            <div key={t.token} className="access-row" style={{ justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: 'var(--navy)' }}>{fmtExpiry(t)}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn--secondary btn--sm" onClick={() => copyLink(t.token)}>
                  {copied === t.token ? '✓ Copied!' : '📋 Copy'}
                </button>
                <button className="btn btn--sm btn--danger" onClick={() => handleRevoke(t.token)}>Revoke</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <label style={{
        fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
        cursor: sitter.email ? 'pointer' : 'not-allowed', opacity: sitter.email ? 1 : 0.6,
      }}>
        <input type="checkbox" checked={!!access.googleSignInEnabled} disabled={!sitter.email || savingAccess}
          onChange={toggleGoogleSignIn} />
        Allow Google sign-in with {sitter.email || 'their email — add one above first'}
      </label>
    </div>
  );
}
